import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TMachineMaintenanceState } from '../reducers';
import {
  TFilterSelected,
  TListingFetchReason,
  TREQUEST_ASSIGN_EMPLOYEE,
  TREQUEST_DELETE_LOCATION,
  TRESPONSE_LOCATION_LIST,
} from '@monorepo/types';
import * as LocationManagementActions from './location-management.actions';
import * as LocationManagementSelector from './location-management.selectors';
import { TLiftedFormModel, TUserPaginationOption } from '@monorepo/ui';
import { LIST_ITEMS_PER_PAGE_LIST } from '../../constants/constants';
import { LOCAL_STORAGE_UTILS } from '@monorepo/utils';
import { TSortingParameter } from 'libs/ui/src/lib/listing-sorter/sorter.types';

@Injectable({
  providedIn: 'root',
})
export class LocationManagementFacade {
  constructor(private store: Store<TMachineMaintenanceState>) {
    this.initializePagination();
  }

  initializePagination() {
    const storedPageSize = LOCAL_STORAGE_UTILS.retrieveData('locationListPageSize');
    if (storedPageSize && LIST_ITEMS_PER_PAGE_LIST.includes(Number(storedPageSize))) {
      this.setPagination({
        pageNo: 1,
        pageSize: parseInt(storedPageSize),
      });
    } else {
      LOCAL_STORAGE_UTILS.storeData('locationListPageSize', LIST_ITEMS_PER_PAGE_LIST[0].toString());
      this.setPagination({
        pageNo: 1,
        pageSize: LIST_ITEMS_PER_PAGE_LIST[0],
      });
    }
  }

  setPagination(pagination: TUserPaginationOption) {
    LOCAL_STORAGE_UTILS.storeData('locationListPageSize', pagination.pageSize.toString());
    this.store.dispatch(new LocationManagementActions.SetLocationListPagination(pagination));
  }

  resetLocationInfo(fetchReason: TListingFetchReason) {
    const pageSize = Number(LOCAL_STORAGE_UTILS.retrieveData('locationListPageSize') || LIST_ITEMS_PER_PAGE_LIST[0]);
    this.setPagination({ pageNo: 1, pageSize });
    this.fetchLocationListInfo([], fetchReason);
  }

  fetchLocationListInfo(
    selectedFilters: TFilterSelected[] = [],
    fetchReason: TListingFetchReason,
    selectedSorting?: TSortingParameter,
  ) {
    this.store.dispatch(
      new LocationManagementActions.GetLocationListInfo(selectedFilters, fetchReason, selectedSorting),
    );
  }

  fetchAssignEmployeeDropdown(locationIds: TRESPONSE_LOCATION_LIST['locationDetails'][number]['id'][]) {
    this.store.dispatch(new LocationManagementActions.GetAssignEmployeeDropdown(locationIds));
  }

  assignEmployee(payload: TREQUEST_ASSIGN_EMPLOYEE) {
    this.store.dispatch(new LocationManagementActions.AssignEmployee(payload));
  }

  fetchFloatingAreaFloorsForLocationDeletion() {
    this.store.dispatch(new LocationManagementActions.GetFloatingAreaFloorsForLocationDeletion());
  }

  deleteLocationListRow(payload: Omit<TREQUEST_DELETE_LOCATION, 'factoryId'>) {
    this.store.dispatch(new LocationManagementActions.DeleteLocations(payload));
  }

  updateLocationManagementPageNo(payload: number) {
    this.store.dispatch(new LocationManagementActions.UpdateLocationManagementPageNo(payload));
  }

  fetchLocalNeedleStores() {
    this.store.dispatch(new LocationManagementActions.GetLocalNeedleStores());
  }

  assignNeedleStores(payload: TLiftedFormModel['payload']) {
    this.store.dispatch(new LocationManagementActions.AssignNeedleStores(payload));
  }

  getLocationListInfo() {
    return this.store.pipe(select(LocationManagementSelector.getLocationListInfo));
  }

  getAssignEmployeeDropDownInfo() {
    return this.store.pipe(select(LocationManagementSelector.getAssignEmployeeDropdownInfo));
  }

  getFloatingAreaFloorsForLocationDeletion() {
    return this.store.pipe(select(LocationManagementSelector.getFloatingAreaFloorsForLocationDeletion));
  }

  getLocalNeedleStores() {
    return this.store.pipe(select(LocationManagementSelector.getLocalNeedleStores));
  }

  getPagination() {
    return this.store.pipe(select(LocationManagementSelector.getPagination));
  }
}

export type TGetLocationListInfo = ReturnType<LocationManagementFacade['getLocationListInfo']>;
export type TGetAssignEmployeeDropDownInfo = ReturnType<LocationManagementFacade['getAssignEmployeeDropDownInfo']>;
export type TGetLocalNeedleStores = ReturnType<LocationManagementFacade['getLocalNeedleStores']>;
export type TGetFloatingAreaFloorsForLocationDeletion = ReturnType<
  LocationManagementFacade['getFloatingAreaFloorsForLocationDeletion']
>;
export type TGetLocationListPagination = ReturnType<LocationManagementFacade['getPagination']>;
